import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, TableSkeleton } from "../../../components/ui";
import { apiError, getPaymentDetail, giveGift } from "../../../services/paymentsLedger";
import RefundModal from "./RefundModal";
import { formatMoney, formatWhen, MethodLabel, StatusBadge, TypeLabel } from "./shared";

const Row = ({ label, children }) =>
  children === null || children === undefined || children === "" ? null : (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );

const REFUND_STATUS = {
  succeeded: "Refunded",
  pending: "Being processed",
  failed: "Refund failed",
  manual: "Recorded (done at the provider)",
};

/** All the details of a payment, the payer's other payments, thanks and gifts. */
const PaymentDetail = ({ id, canAct, onClose, onThank, onChange }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [days, setDays] = useState("30");
  const [giving, setGiving] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(
    () =>
      getPaymentDetail(id)
        .then(setData)
        .catch(() => {
          toast.error(t("Error fetching data"));
          onClose();
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );
  useEffect(() => {
    load();
  }, [load]);

  const gift = async () => {
    setGiving(true);
    try {
      const result = await giveGift(id, Number(days));
      toast.success(t("giftGiven", { count: result.gift.days }));
      await load();
      onChange();
    } catch (error) {
      toast.error(apiError(error) || t("Error updating data"));
    } finally {
      setGiving(false);
    }
  };

  const payment = data?.payment;
  const payer = payment?.payer;
  const giftable = payer?.registered || Boolean(data?.laterAccount);

  return (
    <Modal show={true} onHide={onClose} centered size="lg" dialogClassName="ui-modal">
      <Modal.Header>
        <Modal.Title>{t("Payment details")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!payment ? (
          <TableSkeleton rows={6} />
        ) : (
          <div className="ledger-detail">
            <div className="ledger-detail-head">
              <strong className="ledger-detail-amount">{formatMoney(payment.amount, payment.currency)}</strong>
              <StatusBadge status={payment.status} />
              <TypeLabel payment={payment} />
              {payment.refundedAmount > 0 && (
                <Badge color="red">
                  <i className="ri-arrow-go-back-line" aria-hidden="true"></i>
                  {payment.refundedAmount >= payment.amount
                    ? t("Refunded")
                    : t("{{amount}} refunded", { amount: formatMoney(payment.refundedAmount, payment.currency) })}
                </Badge>
              )}
            </div>

            <section>
              <h3>{t("Payer")}</h3>
              <dl className="ledger-dl">
                <Row label={t("Name")}>{payer.name || "—"}</Row>
                <Row label={t("email")}>{payer.email ? <a href={`mailto:${payer.email}`}>{payer.email}</a> : "—"}</Row>
                <Row label={t("Phone Number")}>{payer.phone}</Row>
                <Row label={t("Account")}>
                  {payer.registered ? (
                    <Badge color="blue">{t("Registered user")}</Badge>
                  ) : (
                    <Badge color="slate">{t("No account")}</Badge>
                  )}
                  {data.laterAccount && (
                    <span className="ui-hint"> {t("Account created since: {{name}}", { name: data.laterAccount.name })}</span>
                  )}
                </Row>
                <Row label={t("Subscription")}>
                  {payer.subscription && (payer.subscription === "paid" ? <Badge color="green">{t("Premium")}</Badge> : t("Free account"))}
                </Row>
              </dl>
            </section>

            <section>
              <h3>{t("Payment")}</h3>
              <dl className="ledger-dl">
                <Row label={t("Date")}>{formatWhen(payment.createdAt)}</Row>
                <Row label={t("Payment method")}><MethodLabel payment={payment} /></Row>
                <Row label={t("Purchased content")}>{payment.content}</Row>
                <Row label={t("Transaction")}>{payment.transactionId && <code>{payment.transactionId}</code>}</Row>
                <Row label={t("Receipt")}>
                  {payment.receiptUrl && (
                    <a href={payment.receiptUrl} target="_blank" rel="noreferrer">
                      {t("Open the receipt")} <i className="ri-share-box-line" aria-hidden="true"></i>
                    </a>
                  )}
                </Row>
                {payment.metadata &&
                  Object.entries(payment.metadata).map(([key, value]) => (
                    <Row key={key} label={key}>{String(value)}</Row>
                  ))}
              </dl>
            </section>

            <section>
              <h3>{t("Thanks and gifts")}</h3>
              {!payment.thanks.length && !payment.gifts.length ? (
                <p className="ui-hint">{t("Not thanked yet.")}</p>
              ) : (
                <ul className="ledger-timeline">
                  {payment.thanks.map((item) => (
                    <li key={`t${item.at}`}>
                      <i className="ri-mail-check-line" aria-hidden="true"></i>
                      {formatWhen(item.at)} · {item.mode === "custom" ? t("Written message") : t("Letter from Alain Foka")} · « {item.subject} » · {item.to}
                    </li>
                  ))}
                  {payment.gifts.map((item) => (
                    <li key={`g${item.at}`}>
                      <i className="ri-award-line" aria-hidden="true"></i>
                      {formatWhen(item.at)} · {t("giftGiven", { count: item.days })}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {payment.refunds?.length > 0 && (
              <section>
                <h3>{t("Refunds")}</h3>
                <ul className="ledger-timeline ledger-refunds">
                  {payment.refunds.map((item) => (
                    <li key={`r${item.at}`} data-status={item.status}>
                      <i className="ri-arrow-go-back-line" aria-hidden="true"></i>
                      {formatWhen(item.at)} · <strong>{formatMoney(item.amount, item.currency)}</strong> · {t(REFUND_STATUS[item.status] || item.status)}
                      {item.reason ? ` · ${item.reason}` : ""}
                      {item.endedSubscription ? ` · ${t("subscription ended")}` : ""}
                      {item.error ? ` · ${item.error}` : ""}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {data.history.length > 0 && (
              <section>
                <h3>{t("historyCount", { count: data.history.length })}</h3>
                <ul className="ledger-history">
                  {data.history.map((item) => (
                    <li key={item.id}>
                      <span className="ui-hint">{formatWhen(item.createdAt)}</span>
                      <TypeLabel payment={item} />
                      <strong>{formatMoney(item.amount, item.currency)}</strong>
                      <StatusBadge status={item.status} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {canAct && payment.status === "SUCCESS" && (
              <section className="ledger-actions">
                <button type="button" className="ui-btn" disabled={!payer.email} onClick={() => onThank(payment)}>
                  <i className="ri-heart-line" aria-hidden="true"></i>
                  {payment.thanks.length ? t("Thank again") : t("Thank")}
                </button>
                <div className="ledger-gift">
                  <select className="ui-input" value={days} aria-label={t("Days offered")} disabled={!giftable} onChange={(e) => setDays(e.target.value)}>
                    {(data.giftDays || [7, 30, 90, 365]).map((value) => (
                      <option key={value} value={value}>
                        {t("daysCount", { count: value })}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="ui-btn ui-btn-secondary" disabled={!giftable || giving} onClick={gift}>
                    <i className="ri-award-line" aria-hidden="true"></i>
                    {t("Offer a subscription")}
                  </button>
                </div>
                {data.refund?.possible && (
                  <button type="button" className="ui-btn ui-btn-secondary ledger-refund-btn" onClick={() => setRefunding(true)}>
                    <i className="ri-arrow-go-back-line" aria-hidden="true"></i>
                    {t("Refund")}
                  </button>
                )}
                {!payer.email && <p className="ui-hint">{t("No email address: this payer cannot be thanked by email.")}</p>}
                {!giftable && <p className="ui-hint">{t("No AFO MEDIA account: a subscription cannot be offered. Thank them by email.")}</p>}
              </section>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
          {t("Close")}
        </button>
      </Modal.Footer>
      {refunding && (
        <RefundModal
          payment={payment}
          info={data.refund}
          onClose={() => setRefunding(false)}
          onDone={async () => {
            setRefunding(false);
            await load();
            onChange();
          }}
        />
      )}
    </Modal>
  );
};

export default PaymentDetail;
